import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface TimelineEvent {
  id?: string;
  event_date: string;
  title: string;
  description: string;
  type: 'critical' | 'declaration' | 'response';
}

const TYPE_LABELS: Record<string, string> = {
  critical: '긴급',
  declaration: '선언',
  response: '대응',
};

const TYPE_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  declaration: 'bg-amber-100 text-amber-700',
  response: 'bg-gray-100 text-gray-700',
};

export default function TimelineSection() {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [newEvent, setNewEvent] = useState<{
    event_date: string;
    title: string;
    description: string;
    type: string;
  }>({
    event_date: '',
    title: '',
    description: '',
    type: 'response',
  });

  // -------------------------------------------------
  // Fetch events from Supabase
  // -------------------------------------------------
  useEffect(() => {
    fetchEvents();
  }, []);

  // Auto‑dismiss toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('timeline_events')
        .select('*')
        .order('event_date', { ascending: false });

      if (error) {
        console.error('Error fetching timeline events:', error);
        setToast({ type: 'error', text: '타임라인을 불러오는 데 실패했습니다.' });
      } else if (data) {
        // Ensure data conforms to TimelineEvent[]
        setEvents(data as TimelineEvent[]);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setToast({ type: 'error', text: '예기치 못한 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------
  // Admin password verification
  // -------------------------------------------------
  const handlePasswordSubmit = () => {
    if (adminPassword === '100-justice') {
      setIsAuthenticated(true);
      setShowPasswordModal(false);
      setShowAddForm(true);
      setAdminPassword('');
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const handleToggleAddForm = () => {
    if (showAddForm) {
      setShowAddForm(false);
      setIsAuthenticated(false);
    } else {
      setShowPasswordModal(true);
      setAdminPassword('');
      setPasswordError(false);
    }
  };

  // -------------------------------------------------
  // Add a new event
  // -------------------------------------------------
  const handleAddEvent = async () => {
    if (!newEvent.event_date || !newEvent.title.trim() || !newEvent.description.trim()) {
      setToast({ type: 'error', text: '모든 필드를 입력해주세요.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('timeline_events').insert({
        event_date: newEvent.event_date,
        title: newEvent.title.trim(),
        description: newEvent.description.trim(),
        type: newEvent.type,
      });

      if (error) {
        console.error('Insert error:', error);
        setToast({ type: 'error', text: '추가에 실패했습니다.' });
      } else {
        setToast({ type: 'success', text: '타임라인이 추가되었습니다.' });
        setNewEvent({ event_date: '', title: '', description: '', type: 'response' });
        setShowAddForm(false);
        await fetchEvents();
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setToast({ type: 'error', text: '오류가 발생했습니다.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // -------------------------------------------------
  // Delete an event
  // -------------------------------------------------
  const handleDeleteEvent = async (id: string) => {
    if (!id) return;
    if (!isAuthenticated) {
      setToast({ type: 'error', text: '관리자 인증이 필요합니다.' });
      return;
    }
    setDeletingId(id);
    try {
      const { error } = await supabase.from('timeline_events').delete().eq('id', id);
      if (error) {
        console.error('Delete error:', error);
        setToast({ type: 'error', text: '삭제에 실패했습니다.' });
      } else {
        setToast({ type: 'success', text: '타임라인이 삭제되었습니다.' });
        await fetchEvents();
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setToast({ type: 'error', text: '오류가 발생했습니다.' });
    } finally {
      setDeletingId(null);
    }
  };

  // -------------------------------------------------
  // Helper – format date
  // -------------------------------------------------
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // -------------------------------------------------
  // Render
  // -------------------------------------------------
  return (
    <section id="timeline" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4"
            style={{ fontFamily: "'Nanum Myeongjo', serif" }}
          >
            타임라인
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-4 md:mb-6"></div>
          <p className="text-lg md:text-xl text-gray-600">사법장악 3법 관련 주요 일정</p>
        </div>

        {/* Admin: Add button */}
        <div className="mb-6 md:mb-8 flex justify-end">
          <button
            onClick={handleToggleAddForm}
            className="flex items-center space-x-2 bg-red-600 text-white px-4 md:px-5 py-2 md:py-2.5 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap shadow-md"
          >
            <i className={`ri-${showAddForm ? 'close' : 'add'}-line text-lg`}></i>
            <span>{showAddForm ? '취소' : '새 타임라인 추가'}</span>
          </button>
        </div>

        {/* Password Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowPasswordModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl p-5 md:p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center mb-4 md:mb-5">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full mr-3">
                  <i className="ri-lock-line text-red-600 text-lg"></i>
                </div>
                <h3 className="text-base md:text-lg font-bold text-gray-900">관리자 인증</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">타임라인을 관리하려면 관리자 비밀번호를 입력해주세요.</p>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => {
                  setAdminPassword(e.target.value);
                  setPasswordError(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handlePasswordSubmit();
                }}
                placeholder="관리자 비밀번호"
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm ${
                  passwordError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
                autoFocus
              />
              {passwordError && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center">
                  <i className="ri-error-warning-line mr-1"></i>
                  비밀번호가 올바르지 않습니다.
                </p>
              )}
              <div className="flex justify-end space-x-3 mt-5">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  onClick={handlePasswordSubmit}
                  className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Form */}
        {showAddForm && isAuthenticated && (
          <div className="mb-8 md:mb-10 bg-white rounded-xl shadow-lg p-5 md:p-6 border border-red-100">
            <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4 md:mb-5 flex items-center">
              <i className="ri-calendar-event-line text-red-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
              새 타임라인 추가
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">날짜</label>
                <input
                  type="date"
                  value={newEvent.event_date}
                  onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">유형</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="critical">긴급</option>
                  <option value="declaration">선언</option>
                  <option value="response">대응</option>
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">제목</label>
              <input
                type="text"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="타임라인 제목을 입력하세요"
                maxLength={100}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">설명</label>
              <textarea
                value={newEvent.description}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setNewEvent({ ...newEvent, description: e.target.value });
                  }
                }}
                placeholder="상세 설명을 입력하세요"
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {newEvent.description.length}/500
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleAddEvent}
                disabled={isSubmitting}
                className="flex items-center space-x-2 bg-red-600 text-white px-5 md:px-6 py-2 md:py-2.5 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:bg-gray-400 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>추가 중...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    <span>추가하기</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-red-200"></div>

          {loading ? (
            <div className="flex justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-3xl text-red-600"></i>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <i className="ri-calendar-line text-4xl mb-3 block"></i>
              <p>등록된 타임라인이 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              {events.map((event, index) => (
                <div key={event.id || index} className="relative pl-12 md:pl-20 group">
                  <div className="absolute left-2.5 md:left-5 top-2 w-4 h-4 md:w-6 md:h-6 bg-red-600 rounded-full border-2 md:border-4 border-white shadow-lg"></div>

                  <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 hover:shadow-xl transition-shadow">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-0 mb-3">
                      <span className="text-xs md:text-sm font-semibold text-red-600">
                        {formatDate(event.event_date)}
                      </span>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2.5 md:px-3 py-0.5 md:py-1 rounded-full text-xs font-semibold ${
                            TYPE_STYLES[event.type] || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {TYPE_LABELS[event.type] || '대응'}
                        </span>
                        {isAuthenticated && event.id && (
                          <button
                            onClick={() => handleDeleteEvent(event.id!)}
                            disabled={deletingId === event.id}
                            className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 cursor-pointer"
                            title="삭제"
                          >
                            {deletingId === event.id ? (
                              <i className="ri-loader-4-line animate-spin text-sm"></i>
                            ) : (
                              <i className="ri-delete-bin-line text-sm"></i>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-4 md:right-6 z-50 animate-fade-in">
          <div
            className={`flex items-center space-x-2 px-4 md:px-5 py-2.5 md:py-3 rounded-lg shadow-xl text-sm font-medium ${
              toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}
          >
            <i
              className={
                toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'
              }
            ></i>
            <span>{toast.text}</span>
          </div>
        </div>
      )}
    </section>
  );
}
